import React, { useState } from 'react';
import ToolLayout from '../../components/ToolLayout';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Copy, FileJson } from 'lucide-react';

const JSONFormatterPage = () => {
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, 2);
      setOutput(formatted);
      setError('');
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
      setOutput('');
    }
  };

  const minifyJSON = () => {
    try {
      const parsed = JSON.parse(input);
      const minified = JSON.stringify(parsed);
      setOutput(minified);
      setError('');
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
      setOutput('');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    toast({
      title: 'Copied!',
      description: 'Output copied to clipboard',
    });
  };

  const clear = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  return (
    <ToolLayout
      title="JSON Formatter"
      description="Format, validate, and beautify JSON data with syntax highlighting. Supports both formatting and minification."
      icon={FileJson}
    >
      <div className="space-y-6">
        <div>
          <Label htmlFor="input">Input JSON</Label>
          <Textarea
            id="input"
            placeholder='Paste your JSON here... e.g., {"name":"John","age":30}'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={10}
            className="font-mono text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={formatJSON}>Format (Beautify)</Button>
          <Button onClick={minifyJSON} variant="secondary">Minify</Button>
          <Button onClick={clear} variant="outline">Clear</Button>
        </div>

        {error && (
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {output && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Output</Label>
              <Button onClick={copyToClipboard} variant="ghost" size="sm">
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
            <Textarea
              value={output}
              readOnly
              rows={10}
              className="font-mono text-sm bg-gray-50 dark:bg-gray-900"
            />
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

export default JSONFormatterPage;